import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import Layout from "../../components/Layout";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Tag, Plus, X, Power, Trash2, Calendar, Percent } from "lucide-react";

/**
 * AdminKampanjekoder – Administrer flergangs prosent-rabatt-koder.
 * Owner/admin only. Brukes til influencer-koder, sesong-kampanjer, vinn-kampanjer osv.
 */
export default function AdminKampanjekoder({ embedded = false }) {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, active_now: 0 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const isAdmin = me?.role === "owner" || me?.role === "admin";
  const Wrap = embedded ? React.Fragment : Layout;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/campaign-codes");
      setItems(data.items || []);
      setCounts(data.counts || { total: 0, active_now: 0 });
    } catch (e) {
      toast.error("Kunne ikke laste kampanje-koder");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [isAdmin]);

  const toggle = async (id, currentActive) => {
    try {
      await api.patch(`/admin/campaign-codes/${id}/toggle`, { active: !currentActive });
      toast.success(currentActive ? "Deaktivert" : "Aktivert");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id, code) => {
    if (!window.confirm(`Slette koden ${code}? Dette kan ikke angres.`)) return;
    try {
      await api.delete(`/admin/campaign-codes/${id}`);
      toast.success("Slettet");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!isAdmin) {
    return (
      <Wrap>
        <div className="text-center text-[var(--cream-soft)] py-20">
          Du må være admin for å se denne siden.
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="max-w-5xl mx-auto" data-testid="admin-campaign-codes-page">
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="font-meta text-[10px] tracking-[0.3em] text-[var(--magenta)] mb-1 flex items-center gap-2">
              <Tag className="h-3 w-3" /> ADMIN · KAMPANJEKODER
            </div>
            <h1 className="font-fraunces italic text-4xl text-[var(--cream)]">Kampanjekoder</h1>
            <p className="text-sm text-[var(--cream-soft)] mt-2 max-w-2xl">
              Flergangs prosent-rabatt på kjøp (chips, VIP, eller alt). Bruk for
              influencer-koder, sesong-kampanjer, gjenåpnings-bonuser osv.
            </p>
          </div>
          <button
            onClick={() => setCreating(true)}
            className="btn-magenta inline-flex items-center gap-2"
            data-testid="new-campaign-code-btn"
          >
            <Plus className="h-3.5 w-3.5" /> Ny kampanjekode
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="editorial-card p-4">
            <div className="font-meta">Totalt</div>
            <div className="font-fraunces text-3xl text-[var(--cream)]">{counts.total}</div>
          </div>
          <div className="editorial-card p-4">
            <div className="font-meta">Aktive nå</div>
            <div className="font-fraunces text-3xl text-[var(--magenta)]">{counts.active_now}</div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[var(--cream-soft)] italic font-fraunces">
            Laster…
          </div>
        ) : items.length === 0 ? (
          <div className="editorial-card p-8 text-center">
            <Tag className="h-8 w-8 mx-auto mb-3 text-[var(--cream-faint)]" />
            <p className="font-fraunces italic text-[var(--cream-soft)]">
              Ingen kampanjekoder ennå. Lag den første.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="campaign-codes-list">
            {items.map((c) => (
              <CodeRow key={c.id} code={c} onToggle={toggle} onRemove={remove} />
            ))}
          </div>
        )}

        {creating && (
          <CreateModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
        )}
      </div>
    </Wrap>
  );
}

function CodeRow({ code, onToggle, onRemove }) {
  const expired = code.valid_until && new Date(code.valid_until) < new Date();
  const fullyUsed = code.max_uses != null && code.use_count >= code.max_uses;
  const status = !code.active
    ? { label: "DEAKTIVERT", color: "text-zinc-500" }
    : expired
    ? { label: "UTLØPT", color: "text-amber-500" }
    : fullyUsed
    ? { label: "BRUKT OPP", color: "text-amber-500" }
    : { label: "AKTIV", color: "text-emerald-500" };
  return (
    <div
      className="editorial-card p-4 flex flex-wrap items-center gap-4"
      data-testid={`campaign-code-row-${code.code}`}
    >
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-3 mb-1">
          <span className="font-mono text-[var(--cream)] text-lg tracking-wider">{code.code}</span>
          <span className={`font-meta ${status.color}`}>{status.label}</span>
        </div>
        <div className="flex items-center gap-4 text-xs text-[var(--cream-soft)] font-manrope">
          {code.kind === "free_vip" ? (
            <span className="text-[var(--magenta)] inline-flex items-center gap-1">
              ★ Gratis VIP · {code.vip_days || 0} dager
            </span>
          ) : (
            <>
              <span className="text-[var(--magenta)] inline-flex items-center gap-1">
                <Percent className="h-3 w-3" /> {code.discount_pct}%
              </span>
              <span className="uppercase tracking-wider text-[10px]">
                {code.applies_to === "chips" ? "Chips" : code.applies_to === "vip" ? "VIP" : "Alle kjøp"}
              </span>
            </>
          )}
          <span>
            {code.use_count} / {code.max_uses == null ? "∞" : code.max_uses} brukt
          </span>
          {code.valid_until && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(code.valid_until).toLocaleDateString("nb-NO")}
            </span>
          )}
        </div>
        {code.note && (
          <div className="text-xs italic text-[var(--cream-faint)] mt-1">{code.note}</div>
        )}
      </div>
      <button
        onClick={() => onToggle(code.id, code.active)}
        className="btn-ghost"
        title={code.active ? "Deaktiver" : "Aktiver"}
        data-testid={`campaign-toggle-${code.code}`}
      >
        <Power className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={() => onRemove(code.id, code.code)}
        className="text-[var(--magenta)] hover:text-[var(--magenta)]/70 transition-colors"
        title="Slett"
        data-testid={`campaign-delete-${code.code}`}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    code: "",
    kind: "discount",
    discount_pct: 10,
    vip_days: 30,
    applies_to: "chips",
    valid_until: "",
    max_uses: "",
    note: "",
    owner_username: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        kind: form.kind,
        discount_pct: form.kind === "discount" ? parseInt(form.discount_pct, 10) : 0,
        vip_days: form.kind === "free_vip" ? parseInt(form.vip_days, 10) : 0,
        applies_to: form.kind === "free_vip" ? "vip" : form.applies_to,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        note: form.note,
        owner_username: form.owner_username.trim() || null,
      };
      await api.post("/admin/campaign-codes", payload);
      toast.success(`Kode ${payload.code} opprettet`);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="editorial-card p-6 w-full max-w-md space-y-4"
        data-testid="create-campaign-modal"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">Ny kampanjekode</h2>
          <button type="button" onClick={onClose} className="text-[var(--cream-soft)] hover:text-[var(--cream)]">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div>
          <div className="font-meta mb-1">Kode</div>
          <input
            type="text"
            value={form.code}
            onChange={(e) => setForm((s) => ({ ...s, code: e.target.value.toUpperCase() }))}
            className="input-editorial uppercase tracking-wider"
            placeholder="SOMMER25"
            required
            minLength={3}
            maxLength={24}
            data-testid="campaign-code-input"
          />
        </div>

        <div>
          <div className="font-meta mb-1">Type</div>
          <select
            value={form.kind}
            onChange={(e) => setForm((s) => ({ ...s, kind: e.target.value }))}
            className="input-editorial w-full"
            data-testid="campaign-kind-input"
          >
            <option value="discount">Rabatt % på kjøp</option>
            <option value="free_vip">Gratis VIP-abonnement</option>
          </select>
        </div>

        {form.kind === "discount" ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="font-meta mb-1">Rabatt %</div>
              <input
                type="number"
                value={form.discount_pct}
                onChange={(e) => setForm((s) => ({ ...s, discount_pct: e.target.value }))}
                className="input-editorial"
                min={1}
                max={100}
                required
                data-testid="campaign-discount-input"
              />
            </div>
            <div>
              <div className="font-meta mb-1">Gjelder</div>
              <select
                value={form.applies_to}
                onChange={(e) => setForm((s) => ({ ...s, applies_to: e.target.value }))}
                className="input-editorial"
                data-testid="campaign-applies-input"
              >
                <option value="chips">Chips-kjøp</option>
                <option value="vip">VIP-abonnement</option>
                <option value="all">Alle kjøp</option>
              </select>
            </div>
          </div>
        ) : (
          <div>
            <div className="font-meta mb-1">VIP-dager (gratis)</div>
            <input
              type="number"
              value={form.vip_days}
              onChange={(e) => setForm((s) => ({ ...s, vip_days: e.target.value }))}
              className="input-editorial"
              min={1}
              max={3650}
              required
              data-testid="campaign-vip-days-input"
            />
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-1">
              Mottaker løser inn koden via «Mine kampanjekoder» og får VIP gratis i N dager.
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="font-meta mb-1">Utløper</div>
            <input
              type="datetime-local"
              value={form.valid_until}
              onChange={(e) => setForm((s) => ({ ...s, valid_until: e.target.value }))}
              className="input-editorial"
              data-testid="campaign-expiry-input"
            />
          </div>
          <div>
            <div className="font-meta mb-1">Maks bruk</div>
            <input
              type="number"
              value={form.max_uses}
              onChange={(e) => setForm((s) => ({ ...s, max_uses: e.target.value }))}
              className="input-editorial"
              min={1}
              placeholder="∞ ubegrenset"
              data-testid="campaign-maxuses-input"
            />
          </div>
        </div>

        <div>
          <div className="font-meta mb-1">Tilordne til influencer (valgfritt)</div>
          <input
            type="text"
            value={form.owner_username}
            onChange={(e) => setForm((s) => ({ ...s, owner_username: e.target.value }))}
            className="input-editorial"
            placeholder="@brukernavn"
            maxLength={50}
            data-testid="campaign-owner-input"
          />
          <p className="font-manrope text-[10px] text-[var(--cream-faint)] mt-1 uppercase tracking-[0.15em]">
            brukeren ser koden under «mine kampanjekoder»
          </p>
        </div>

        <div>
          <div className="font-meta mb-1">Notat (valgfritt)</div>
          <input
            type="text"
            value={form.note}
            onChange={(e) => setForm((s) => ({ ...s, note: e.target.value }))}
            className="input-editorial"
            maxLength={200}
            placeholder="Maja-influencer sommer 2026"
            data-testid="campaign-note-input"
          />
        </div>

        <div className="flex justify-end gap-3 pt-3">
          <button type="button" onClick={onClose} className="btn-ghost">
            Avbryt
          </button>
          <button type="submit" disabled={saving} className="btn-magenta" data-testid="campaign-create-submit">
            {saving ? "Oppretter…" : "Opprett"}
          </button>
        </div>
      </form>
    </div>
  );
}
